import axios from "axios";

/**
 * Service API pour la gestion des projections de contrats.
 * Permet aux commerciaux (REPS) de créer des simulations de contrats
 * basées sur l'historique d'achats des clients.
 */
export default {
  /**
   * Récupère la liste des clients éligibles pour une projection.
   * Les filtres sont appliqués côté back-end :
   * - Clients sans contrat actif
   * - Clients assignés à l'utilisateur (pour les REPS)
   * - Tous les clients (pour les ADMIN)
   *
   * @returns {Promise} Liste des clients avec leurs informations de base
   */
  getEligibleCustomers() {
    return axios.get("/api/projections/customers");
  },

  /**
   * Calcule une projection de contrat basée sur les données saisies.
   * Les points sont calculés automatiquement par le back-end.
   *
   * @param {Object} projectionData - Données de la projection
   * @param {number} projectionData.customerId - ID du client
   * @param {number} projectionData.projectionYear - Année de projection (année en cours)
   * @param {Object} projectionData.capitalInput - Entrée pour capital (équipements)
   * @param {string} projectionData.capitalInput.inputType - Type d'entrée ("EurosAmount")
   * @param {number} projectionData.capitalInput.value - Montant en euros
   * @param {Object} projectionData.implantInput - Entrée pour implants
   * @param {string} projectionData.implantInput.inputType - Type d'entrée ("EurosAmount")
   * @param {number} projectionData.implantInput.value - Montant en euros
   * @param {Object} projectionData.serviceInput - Entrée pour services
   * @param {string} projectionData.serviceInput.inputType - Type d'entrée ("EurosAmount")
   * @param {number} projectionData.serviceInput.value - Montant en euros
   * @param {Object} projectionData.consommableInput - Entrée pour consommables
   * @param {string} projectionData.consommableInput.inputType - Type d'entrée ("EurosAmount")
   * @param {number} projectionData.consommableInput.value - Montant en euros
   * @returns {Promise} Résultats de la projection avec stats actuelles et projetées
   */
  calculate(projectionData) {
    return axios.post("/api/projections/calculate", projectionData);
  },

  /**
   * Exporte les projections en PDF.
   * @param {Object} exportData - Données d'export
   * @param {Object} exportData.baseStatistics - Statistiques actuelles (N-1)
   * @param {Array} exportData.scenarios - Liste des scénarios (1 à 5)
   * @param {string} exportData.scenarios[].scenarioName - Nom du scénario
   * @param {Object} exportData.scenarios[].projection - Projection calculée
   * @returns {Promise<Blob>} Fichier PDF
   */
  exportToPdf(exportData) {
    return axios.post("/api/projections/export", exportData, {
      responseType: "blob",
    });
  },

  /**
   * Calcule le nombre de points pour un montant donné selon la catégorie et le statut.
   * Permet une conversion rapide sans créer de projection complète.
   *
   * @param {Object} calculationData - Données de calcul
   * @param {number} calculationData.amountInEuros - Montant en euros à convertir
   * @param {number} calculationData.categoryId - ID de la catégorie (1=Service, 2=Capital, 3=Implant, 4=Consommable)
   * @param {number} calculationData.statusId - ID du statut (1-6)
   * @returns {Promise} Résultat avec points calculés et taux de conversion
   */
  calculatePoints(calculationData) {
    return axios.post("/api/projections/calculate-points", calculationData);
  },
};
